// FICHA DO PRODUTO — redesenhada (W5, plano docs/specs/2026-09-22-redesign-plan.md
// secao 5). A cor virou o eixo de navegacao (README "Ficha de produto"): espinha
// de cores a esquerda (desktop sticky / gaveta mobile), Base do produto e Cor
// selecionada a direita, faixa de prontidao no topo, rodape fixo com as acoes
// de publicar. Toda a logica de negocio (mutations, RN03, RN10) e a MESMA de
// antes do redesign — ver Check 0 no plano secao 6.3. So a moldura mudou.
import { useEffect, useMemo, useState } from "react";
import { pubStatusLabel, jobLabel } from "../lib/status-labels";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, Archive, CheckCircle2, Clock, ExternalLink, History, Info } from "lucide-react";
import { toast } from "sonner";
import { api, apiErrorMessage } from "../lib/api";
import type { Category, ChannelDetail, JobInfo, ProductDetail, PublicationStatus } from "../lib/types";
import { PageHeader } from "../components/layout/PageHeader";
import { usePhotoManager } from "../components/PhotosTab";
import { useGridData } from "../components/GridTab";
import { ColorSpine, type ColorSpineRow } from "../components/product/ColorSpine";
import { ReadinessBar } from "../components/product/ReadinessBar";
import { BaseProductPanel } from "../components/product/BaseProductPanel";
import { ColorPanel } from "../components/product/ColorPanel";
import { computeReadiness } from "../lib/readiness";
import { channelLinks, type ChannelLinks } from "../lib/channel-links";
import { Card, CardContent } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import { EmptyState } from "../components/ui/empty-state";
import { Badge, type BadgeVariant } from "../components/ui/badge";
import { ArchivedBadge } from "../components/ui/ArchivedBadge";
import { Button } from "../components/ui/button";
import { Drawer } from "../components/ui/Drawer";
import { ColorSwatch } from "../components/product/ColorSwatch";
import { MlItemStrip } from "../components/channel/MlItemStrip";
import { useAuth } from "../contexts/AuthContext";
import { formatCurrency, formatDate, relativeTime } from "../lib/utils";

const STATUS_VARIANT: Record<PublicationStatus, BadgeVariant> = {
  live: "live",
  draft: "draft",
  unpublished: "unpublished",
  error: "error",
};

export function ProductPage(): JSX.Element {
  const { slug = "", sku = "" } = useParams<{ slug: string; sku: string }>();
  const qc = useQueryClient();
  const { user } = useAuth();

  // Arquivo solto fora da area de envio: sem isso o navegador abre a imagem e
  // tira a operadora da pagina. So intercepta arquivo do sistema (types inclui
  // "Files"), nunca o arrastar de reordenar fotos.
  useEffect(() => {
    const blockFileDrop = (e: DragEvent): void => {
      if (e.dataTransfer && Array.from(e.dataTransfer.types).includes("Files")) e.preventDefault();
    };
    window.addEventListener("dragover", blockFileDrop);
    window.addEventListener("drop", blockFileDrop);
    return () => {
      window.removeEventListener("dragover", blockFileDrop);
      window.removeEventListener("drop", blockFileDrop);
    };
  }, []);

  const { data: product, isLoading, isError, error, refetch } = useQuery<ProductDetail>({
    queryKey: ["product", slug, sku],
    queryFn: async () => (await api.get<ProductDetail>(`/api/channels/${slug}/products/${sku}`)).data,
    enabled: slug.length > 0 && sku.length > 0,
  });

  // Mesma chave da ChannelPage (cache compartilhado); sem ela, o ml da ficha identifica o canal ML.
  const { data: channel } = useQuery<ChannelDetail>({
    queryKey: ["channel", slug],
    queryFn: async () => (await api.get<ChannelDetail>(`/api/channels/${slug}`)).data,
    enabled: slug.length > 0,
  });
  const links = channelLinks({
    adapter: channel?.adapter ?? (product?.ml ? "ml" : "wc"),
    externalId: product?.publication.external_id,
    mlPermalink: product?.ml?.permalink,
  });

  const { data: categories = [] } = useQuery<Category[]>({
    queryKey: ["categories"],
    queryFn: async () => (await api.get<Category[]>("/api/categories")).data,
  });

  const [activeJobId, setActiveJobId] = useState<string | number | null>(null);
  const { data: activeJob } = useQuery<JobInfo>({
    queryKey: ["job", activeJobId],
    queryFn: async () => (await api.get<JobInfo>(`/api/jobs/${activeJobId}`)).data,
    enabled: activeJobId !== null,
    refetchInterval: (q) => {
      const d = q.state.data;
      if (!d) return 1000;
      return d.status === "queued" || d.status === "running" ? 1000 : false;
    },
  });

  useEffect(() => {
    if (!activeJob) return;
    if (activeJob.status === "done") {
      toast.success(`Job ${activeJob.action} concluído`);
      void refetch();
      void qc.invalidateQueries({ queryKey: ["channel-products"] });
      setActiveJobId(null);
    } else if (activeJob.status === "failed") {
      toast.error(`Job ${activeJob.action} falhou: ${activeJob.error ?? "erro desconhecido"}`);
      void refetch();
      setActiveJobId(null);
    }
  }, [activeJob, refetch, qc]);

  if (isLoading) {
    return (
      <div>
        <Skeleton className="h-10 w-1/2 mb-6" />
        <div className="grid gap-6 lg:grid-cols-[2fr_3fr]">
          <Skeleton className="h-96 w-full" />
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  if (isError || !product) {
    return (
      <EmptyState
        icon={<AlertCircle className="h-12 w-12" />}
        title="Erro ao carregar produto"
        description={(error as Error)?.message ?? "Produto não encontrado ou sem permissão"}
      />
    );
  }

  return (
    <ProductPageBody
      product={product}
      slug={slug}
      sku={sku}
      categories={categories}
      isOperator={!!user}
      activeJob={activeJob ?? null}
      onJobStart={(id) => setActiveJobId(id)}
      links={links}
    />
  );
}

function ProductPageBody({
  product,
  slug,
  sku,
  categories,
  activeJob,
  onJobStart,
  links,
}: {
  product: ProductDetail;
  slug: string;
  sku: string;
  categories: Category[];
  links: ChannelLinks;
  isOperator: boolean;
  activeJob: JobInfo | null;
  onJobStart: (id: string | number) => void;
}): JSX.Element {
  // RBAC configuravel (plano 2026-09-23, BUG-01 do QA): a UI espelha a
  // capacidade real do perfil, nao mais um bool generico "esta logado".
  // `disabled` (campos/salvar base+override+SEO+promocao/switch Sobrescrever)
  // segue products.write; fotos e publicacao tem capacidade PROPRIA porque
  // um perfil pode ter uma sem ter a outra (ex.: so photos.write).
  const { can } = useAuth();
  const canWrite = can("products.write");
  const canPublishCap = can("products.publish");
  const canPhotos = can("photos.write");
  const disabled = !canWrite;
  const photosDisabled = !canPhotos;
  // Faixa "somente leitura": so quando o perfil nao tem NENHUMA capacidade de
  // escrita nesta ficha (nem campo, nem foto) — perfil com so photos.write
  // (o caso do BUG-01) ainda tem o que fazer aqui, entao nao e "leitura".
  const readOnly = disabled && photosDisabled;
  const photoMgr = usePhotoManager(product, slug);
  const gridMgr = useGridData(product, slug);

  const activeColors = useMemo(() => product.sisplan.colors_active ?? [], [product.sisplan.colors_active]);
  const [context, setContext] = useState<"base" | string>("base");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [mobileSpineOpen, setMobileSpineOpen] = useState(false);

  // Se o produto trocar (navegou pra outro SKU), volta pra Base.
  useEffect(() => {
    setContext("base");
    setSelected(new Set());
  }, [product.sku, slug]);

  const rows: ColorSpineRow[] = useMemo(
    () =>
      activeColors.map((name) => {
        const gridColor = gridMgr.data?.colors.find((c) => c.value === name);
        let stock = 0;
        if (gridMgr.data) {
          for (const s of gridMgr.data.sizes) {
            const cell = gridMgr.cellMap.get(`${name}|${s.value}`);
            if (cell) stock += cell.stock;
          }
        }
        const photoCount = photoMgr.colorIds(name).length;
        return {
          name,
          hasOwnPhoto: photoCount > 0,
          photoCount,
          stock,
          disabledInChannel: gridColor ? gridColor.hidden_by !== null : false,
        };
      }),
    [activeColors, photoMgr, gridMgr.data, gridMgr.cellMap],
  );

  const readiness = computeReadiness(product, slug, gridMgr.data ?? null);
  // Produto arquivado (plano 2026-10-27): nada o publica, nem a mao. O motivo vem
  // no title dos botoes; a API recusa com 409 entry_archived se alguem forcar.
  const archived = product.entry.status === "archived";
  // Inativo no Sisplan (03/11/2026): a tela deixava publicar e o cron tirava da loja 10 min depois, em
  // laco. A API recusa com 409 sku_inactive; aqui o botao ja nasce desabilitado com o motivo.
  const sisplanInactive = Boolean(product.sisplan.status) && product.sisplan.status !== "active";
  const publishBlockHint = archived ? ARCHIVED_PUBLISH_HINT : sisplanInactive ? INACTIVE_PUBLISH_HINT : undefined;
  const archivedTitle = archived
    ? `Arquivado${product.entry.archived_by_name ? ` por ${product.entry.archived_by_name}` : ""}${
        product.entry.archived_at ? ` em ${formatDate(product.entry.archived_at)}` : ""
      }`
    : undefined;
  const totalStock = product.sisplan.stock.reduce((sum, s) => sum + (s.qty ?? 0), 0);

  // Republicar direto na faixa de erro — MESMO endpoint/fluxo do rodape
  // (ActionsBar), so um atalho a mais perto do aviso (Check 0: nao substitui
  // o botao do rodape, so soma).
  const quickRepublish = useMutation({
    mutationFn: async () => (await api.post<JobEnqueued>(`/api/channels/${slug}/products/${sku}/republish`)).data,
    onSuccess: (d) => {
      toastJobEnqueued(d, "republicar");
      onJobStart(d.job_id);
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const toggleSelected = (name: string): void => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const batchToggle = (enabled: boolean): void => {
    if (selected.size === 0) return;
    gridMgr.batchMutation.mutate(
      Array.from(selected).map((c) => ({ kind: "color" as const, value: c, enabled })),
    );
  };

  const batchCopyPhotos = (fromColor: string): void => {
    for (const c of selected) {
      if (c === fromColor) continue;
      void photoMgr.copyPhotosFromColor(fromColor, c);
    }
  };

  const applySizesToSelected = (sizes: string[]): void => {
    if (selected.size === 0 || !gridMgr.data) return;
    const rules: { kind: "combo"; value: string; enabled: boolean }[] = [];
    for (const color of selected) {
      for (const size of sizes) {
        const cell = gridMgr.cellMap.get(`${color}|${size}`);
        if (cell) rules.push({ kind: "combo", value: cell.sku_variation, enabled: true });
      }
    }
    if (rules.length > 0) gridMgr.batchMutation.mutate(rules);
  };

  const disableColorInChannel = (color: string): void => {
    gridMgr.toggleMutation.mutate({ kind: "color", value: color, enabled: false });
  };

  const contextIndex = context === "base" ? -1 : activeColors.indexOf(context);
  const prevColor = contextIndex > 0 ? activeColors[contextIndex - 1] ?? null : null;
  const nextColor =
    contextIndex >= 0 && contextIndex < activeColors.length - 1 ? activeColors[contextIndex + 1] ?? null : null;

  const spineProps = {
    rows,
    totalInSisplan: activeColors.length,
    activeContext: context,
    onSelectContext: (ctx: "base" | string) => {
      setContext(ctx);
      setMobileSpineOpen(false);
    },
    selected,
    onToggleSelected: toggleSelected,
    onClearSelected: () => setSelected(new Set()),
    onBatchEnable: () => batchToggle(true),
    onBatchDisable: () => batchToggle(false),
    onBatchCopyPhotos: batchCopyPhotos,
    disabled,
  };

  return (
    <div>
      <PageHeader
        title={product.publication.name_override || product.entry.name_base || product.sisplan.name || product.sku}
        mobileTitle={product.publication.name_override || product.entry.name_base || product.sisplan.name || product.sku}
        description={`SKU ${product.sku} · canal ${slug}`}
        breadcrumb={
          <span>
            <Link to="/" className="hover:underline">Home</Link> ·{" "}
            <Link to={`/canais/${slug}`} className="hover:underline">{slug}</Link>
          </span>
        }
      />

      <div className="cluster" style={{ marginBottom: ".75rem" }}>
        <Badge variant={STATUS_VARIANT[product.publication.status]}>{pubStatusLabel(product.publication.status)}</Badge>
        {archived ? <ArchivedBadge title={archivedTitle} /> : null}
        {sisplanInactive ? (
          <Badge variant="unpublished" title="Produto inativo no Sisplan: não entra na loja. Se não vai voltar, arquive-o.">
            Inativo no Sisplan
          </Badge>
        ) : null}
        {links.externalIdLabel ? (
          <span className="mono field__hint">{links.externalIdLabel}</span>
        ) : null}
        <span style={{ width: ".0625rem", height: "1.25rem", background: "var(--color-line)" }} aria-hidden />
        <ReadinessBar chips={readiness.chips} />
        <Link to={`/canais/${slug}/produtos/${sku}/historico`} className="btn btn--secondary sm:ml-auto">
          <History className="icon" aria-hidden />
          Histórico
        </Link>
      </div>

      {/* Sisplan (read-only) — linha compacta abaixo do cabecalho. Era cartao
          grande dentro da Base do produto; virou 1 linha porque a info vale
          pra ficha inteira (Base OU Cor selecionada), nao so pra Base. */}
      <div
        className="cluster"
        style={{
          marginBottom: ".75rem",
          padding: ".5rem .75rem",
          borderRadius: "var(--radius-md)",
          background: "var(--color-soft)",
          border: ".0625rem solid var(--color-line)",
          fontSize: "var(--text-sm)",
        }}
      >
        <Info className="icon" style={{ width: "0.875em", height: "0.875em", color: "var(--color-ink-2)" }} aria-hidden />
        <span style={{ fontWeight: 800, color: "var(--color-ink-2)" }}>SISPLAN (read-only)</span>
        <span aria-hidden style={{ color: "var(--color-ink-3)" }}>·</span>
        <span style={{ fontWeight: 700 }}>{formatCurrency(product.sisplan.price)}</span>
        <span aria-hidden style={{ color: "var(--color-ink-3)" }}>·</span>
        <span>
          {totalStock.toLocaleString("pt-BR")} un.
          {product.sisplan.stock.length > 0 ? (
            <span className="field__hint"> ({product.sisplan.stock.map((s) => `${s.channel}: ${s.qty}`).join(" · ")})</span>
          ) : null}
        </span>
        <span aria-hidden style={{ color: "var(--color-ink-3)" }}>·</span>
        <span className="field__hint">
          <Clock className="icon" style={{ width: "0.875em", height: "0.875em", display: "inline", verticalAlign: "-0.1em" }} aria-hidden />{" "}
          Última sincronização: {relativeTime(product.publication.last_synced_at)}
        </span>
      </div>

      <MlItemStrip ml={product.ml} />

      {product.publication.last_error ? (
        <Card className="mb-4" style={{ borderColor: "var(--color-bad)", background: "var(--color-bad-soft)" }}>
          <CardContent className="cluster" style={{ alignItems: "center", padding: ".625rem .75rem" }}>
            <AlertCircle className="icon text-bad" style={{ flexShrink: 0 }} aria-hidden />
            <div className="grow" style={{ minWidth: 0 }}>
              <span className="text-md text-bad" style={{ fontWeight: 800 }}>
                Última sincronização falhou · {relativeTime(product.publication.last_synced_at)}
              </span>{" "}
              <span className="mono field__hint break-words">{product.publication.last_error}</span>
            </div>
            {canPublishCap ? (
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={() => quickRepublish.mutate()}
                loading={quickRepublish.isPending}
                disabled={archived || sisplanInactive}
                title={publishBlockHint}
                style={{ flexShrink: 0 }}
              >
                Republicar
              </Button>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      {readOnly ? (
        <div
          className="cluster"
          style={{
            marginBottom: ".75rem",
            padding: ".5rem .75rem",
            borderRadius: "var(--radius-md)",
            background: "var(--color-soft)",
            border: ".0625rem solid var(--color-line)",
            fontSize: "var(--text-sm)",
            color: "var(--color-ink-2)",
          }}
        >
          Somente leitura: seu perfil permite ver este produto.
        </div>
      ) : null}

      {/* Mobile: barra "Editando · <cor>" abre a gaveta com a espinha */}
      {/* Barra FIXA sob o topo no celular (prototipo v2): rotulo "EDITANDO",
          o contexto em negrito e "Trocar" abre a gaveta de cores. Antes ela
          rolava junto e sumia, e o operador perdia em que cor estava. */}
      <button
        type="button"
        className="ctx-bar lg:hidden"
        onClick={() => setMobileSpineOpen(true)}
        aria-label={`Editando ${context === "base" ? "Base do produto" : context}. Trocar cor`}
        style={{
          width: "100%",
          marginBottom: ".75rem",
          borderRadius: "var(--radius-lg)",
          border: "none",
          font: "inherit",
          color: "var(--color-ink)",
          position: "sticky",
          top: 0,
          zIndex: 15,
          boxShadow: "var(--shadow-card)",
        }}
      >
        {context === "base" ? (
          <span aria-hidden style={{ width: "1.75rem", height: "1.75rem", borderRadius: "var(--radius-sm)", background: "var(--color-lilac-soft)", flexShrink: 0 }} />
        ) : (
          <ColorSwatch name={context} />
        )}
        <span className="grow" style={{ textAlign: "left", minWidth: 0 }}>
          <span style={{ display: "block", fontSize: "var(--text-micro)", fontWeight: 800, letterSpacing: ".05em", textTransform: "uppercase", color: "var(--color-ink-3)" }}>
            Editando
          </span>
          <span className="truncate" style={{ display: "block", fontWeight: 900 }}>
            {context === "base" ? "Base do produto" : context}
          </span>
        </span>
        <span className="chip" style={{ background: "var(--color-coral-soft)", color: "var(--color-coral)" }}>Trocar</span>
      </button>

      <Drawer open={mobileSpineOpen} onOpenChange={setMobileSpineOpen} title="Cores">
        <ColorSpine {...spineProps} desktop={false} />
      </Drawer>

      {/* Mobile: 1 coluna (a espinha vira gaveta, ver ctx-bar acima). Desktop
          (>=64rem): 2 colunas fixas. Regra R2 "toda coluna de grade tem piso"
          nao se aplica aqui (2 celulas de layout, nao dado tabular), mas o
          template PRECISA ser condicional — sem isso o item unico da grade
          cai na 1a coluna (4fr) e a 2a (8fr) fica em branco (bug medido no
          smoke W5 em 375px: SISPLAN espremido a ~35% da largura). */}
      <div
        className="grid gap-3 lg:grid-cols-[minmax(0,4fr)_minmax(0,8fr)]"
        style={{ alignItems: "flex-start" }}
      >
        {/* O STICKY mora no item da grade, nao no <aside>: com alignItems
            flex-start o wrapper tinha a mesma altura do aside, e sticky dentro
            de um pai do proprio tamanho nao tem por onde deslizar (a coluna
            rolava junto com a pagina; print do dono 23/09). */}
        <div className="hidden lg:block" style={{ minWidth: 0, position: "sticky", top: "1rem", alignSelf: "start" }}>
          <ColorSpine {...spineProps} desktop />
        </div>

        <div style={{ minWidth: 0 }}>
          {context === "base" ? (
            <BaseProductPanel
              product={product}
              slug={slug}
              categories={categories}
              disabled={disabled}
              photosDisabled={photosDisabled}
              photoMgr={photoMgr}
              gridMgr={gridMgr}
            />
          ) : (
            <ColorPanel
              color={context}
              slug={slug}
              disabled={disabled}
              photosDisabled={photosDisabled}
              stock={rows.find((r) => r.name === context)?.stock ?? 0}
              onEnabled={!(rows.find((r) => r.name === context)?.disabledInChannel ?? false)}
              prevColor={prevColor}
              nextColor={nextColor}
              onNavigate={setContext}
              photoMgr={photoMgr}
              gridMgr={gridMgr}
              selectedForBatch={selected}
              onApplySizesToSelected={applySizesToSelected}
              onDisableColorInChannel={disableColorInChannel}
              onCopyPhotosFrom={(from) => void photoMgr.copyPhotosFromColor(from, context)}
            />
          )}
        </div>
      </div>

      <ActionsBar
        product={product}
        slug={slug}
        sku={sku}
        activeJob={activeJob}
        onJobStart={onJobStart}
        readiness={readiness}
        canPublishCap={canPublishCap}
        archived={archived}
        sisplanInactive={sisplanInactive}
        links={links}
      />
    </div>
  );
}

const ARCHIVED_PUBLISH_HINT = "Produto arquivado: desarquive para publicar.";
const INACTIVE_PUBLISH_HINT = "Produto inativo no Sisplan: não pode ser publicado. Se não vai voltar, arquive-o.";

/** Resposta 202 de publish/unpublish/republish. `already_queued` = ja havia
 * job igual pendente (ex.: salvar um campo enfileira republish sozinho) e a
 * API devolveu esse job em vez de erro. */
interface JobEnqueued {
  job_id: string | number;
  already_queued?: boolean;
}

function toastJobEnqueued(d: JobEnqueued, verb: string): void {
  if (d.already_queued) {
    toast.info(`Já está na fila: ${verb} (job ${d.job_id}). É só aguardar terminar.`);
  } else {
    toast.info(`Job ${d.job_id} enfileirado: ${verb}`);
  }
}

/* ============================================================
 * RODAPE — Publicar/Despublicar/Republicar (Check 0 6.3)
 * Sticky DENTRO da coluna de conteudo (.content), nunca fixed na
 * viewport inteira: fixed+left-0+right-0 cobria o rodape do proprio
 * menu lateral (sair / trocar tema), que fica fora do alcance do
 * usuario enquanto a ficha esta aberta (reprovacao Wladimir 23/09).
 * ============================================================ */
function ActionsBar({
  product,
  slug,
  sku,
  activeJob,
  onJobStart,
  readiness,
  canPublishCap,
  archived,
  sisplanInactive,
  links,
}: {
  product: ProductDetail;
  slug: string;
  sku: string;
  activeJob: JobInfo | null;
  onJobStart: (id: string | number) => void;
  readiness: ReturnType<typeof computeReadiness>;
  canPublishCap: boolean;
  archived: boolean;
  sisplanInactive: boolean;
  links: ChannelLinks;
}): JSX.Element {
  const { canPublish, missing } = readiness;
  const publishBlockHint = archived ? ARCHIVED_PUBLISH_HINT : sisplanInactive ? INACTIVE_PUBLISH_HINT : undefined;
  const isLive = product.publication.status === "live";
  const isError = product.publication.status === "error";
  const jobRunning = !!activeJob && (activeJob.status === "queued" || activeJob.status === "running");

  const publish = useMutation({
    mutationFn: async () => (await api.post<JobEnqueued>(`/api/channels/${slug}/products/${sku}/publish`)).data,
    onSuccess: (d) => {
      toastJobEnqueued(d, "publicar");
      onJobStart(d.job_id);
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const unpublish = useMutation({
    mutationFn: async () => (await api.post<JobEnqueued>(`/api/channels/${slug}/products/${sku}/unpublish`)).data,
    onSuccess: (d) => {
      toastJobEnqueued(d, "despublicar");
      onJobStart(d.job_id);
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const republish = useMutation({
    mutationFn: async () => (await api.post<JobEnqueued>(`/api/channels/${slug}/products/${sku}/republish`)).data,
    onSuccess: (d) => {
      toastJobEnqueued(d, "republicar");
      onJobStart(d.job_id);
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  return (
    <div
      className="content-footer border-t"
      style={{
        borderColor: "var(--color-line)",
        background: "var(--color-card)",
        backdropFilter: "blur(0.25rem)",
        marginTop: "auto",
        borderRadius: "var(--radius-lg) var(--radius-lg) 0 0",
        boxShadow: "var(--shadow-card)",
      }}
    >
      <div className="spread" style={{ padding: ".75rem 1rem", flexWrap: "wrap", gap: ".5rem" }}>
        <div className="cluster" style={{ fontSize: "var(--text-sm)" }}>
          {archived ? (
            <span className="cluster" style={{ color: "var(--color-ink-2)" }}>
              <Archive className="icon" style={{ width: "0.875em", height: "0.875em" }} aria-hidden />
              Produto arquivado: fora da operação
            </span>
          ) : canPublish ? (
            <span className="cluster" style={{ color: "var(--color-good)" }}>
              <CheckCircle2 className="icon" style={{ width: "0.875em", height: "0.875em" }} aria-hidden />
              {isLive ? "Publicado" : "Pronto pra publicar"}
            </span>
          ) : (
            <span className="cluster" style={{ color: "var(--color-warn)" }}>
              <AlertCircle className="icon" style={{ width: "0.875em", height: "0.875em" }} aria-hidden />
              Falta: {missing.join(", ")}
            </span>
          )}
          {jobRunning ? (
            <Badge variant="secondary" className="animate-pulse">
              Tarefa: {jobLabel(activeJob?.action, activeJob?.status)} ({activeJob?.attempts}x)
            </Badge>
          ) : null}
          {isError && links.adminUrl ? (
            <a
              href={links.adminUrl}
              target="_blank"
              rel="noreferrer"
              className="cluster"
              style={{ fontSize: "var(--text-sm)", color: "var(--color-ink-2)" }}
            >
              <ExternalLink className="icon" style={{ width: "0.875em", height: "0.875em" }} aria-hidden /> {links.adminLabel}
            </a>
          ) : null}
        </div>
        {/* Celular: os 3 botoes numa linha so (prototipo v2); o .btn do kit
            pede flex-basis 10rem abaixo de 40rem e quebrava em duas linhas. */}
        <div className="cluster footer-actions" style={{ flexWrap: "nowrap", minWidth: 0 }}>
          {isLive && canPublishCap ? (
            <Button variant="secondary" onClick={() => unpublish.mutate()} loading={unpublish.isPending} disabled={jobRunning}>
              Despublicar
            </Button>
          ) : null}
          {(isLive || isError) && canPublishCap ? (
            <Button
              variant="primary"
              onClick={() => republish.mutate()}
              loading={republish.isPending}
              disabled={archived || sisplanInactive || jobRunning || !canPublish}
              title={publishBlockHint ?? (!canPublish ? `Falta: ${missing.join(", ")}` : links.republishTitle)}
            >
              Republicar
            </Button>
          ) : null}
          {!isLive && canPublishCap ? (
            <Button
              onClick={() => publish.mutate()}
              loading={publish.isPending}
              disabled={archived || sisplanInactive || jobRunning || !canPublish}
              title={publishBlockHint ?? (!canPublish ? `Falta: ${missing.join(", ")}` : links.publishTitle)}
            >
              Publicar
            </Button>
          ) : null}
          {links.viewUrl ? (
            <a
              href={links.viewUrl}
              target="_blank"
              rel="noreferrer"
              className="btn btn--secondary"
            >
              <ExternalLink className="icon" aria-hidden />
              <span className="sm:hidden">{links.viewLabelShort}</span>
              <span className="hidden sm:inline">{links.viewLabel}</span>
            </a>
          ) : null}
        </div>
      </div>
    </div>
  );
}
